import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IExtendedTranscriptChunk,
  TranscriptService,
} from '@techspert-io/transcriptions';
import { combineLatest, of } from 'rxjs';
import { FileStoreService } from '../../../../shared/services/filestore.service';
import {
  IDiscussionGuideQuestion,
  IRespondent,
  IRespondentWithAnswers,
  IRespondentAnswer,
  IRespondentAnswerReference,
  IRespondentFile,
  RespondentSource,
} from '../../models/data-capture.models';

type GroupedTheme = {
  theme: string;
  questions: IDiscussionGuideQuestion[];
};

export interface IRespodentDialogData {
  respondentId: string;
  respondents: IRespondentWithAnswers[];
  groupedThemes: GroupedTheme[];
}

@Component({
  selector: 'app-view-respondent',
  templateUrl: './view-respondent.component.html',
  styleUrls: ['./view-respondent.component.scss'],
  standalone: false,
})
export class ViewRespondentComponent implements OnInit, OnDestroy {
  loading = true;

  selectedRespondent: IRespondentWithAnswers;

  groupedThemes: GroupedTheme[];

  audioStartTime = 0;
  transcriptFile?: IRespondentFile;
  audioData?: string;
  transcriptChunks: IExtendedTranscriptChunk[] = [];

  answerUpdates: Record<string, string> = {};

  get respondentId() {
    if (!this.selectedRespondent) {
      return;
    }

    return this.selectedRespondent.type === RespondentSource.Techspert
      ? this.selectedRespondent.source
      : this.selectedRespondent.respondentId;
  }

  get showPlaybackLanguage() {
    return this.transcriptChunks.some(
      (c) => c.normalisedText && c.normalisedText !== c.text
    );
  }

  get transcriptFileName() {
    const fileName = this.transcriptFile
      ? this.transcriptFile.fileName
      : `${this.selectedRespondent.firstName} - ${this.selectedRespondent.lastName}`;

    return fileName.trim();
  }

  constructor(
    private fileStoreService: FileStoreService,
    private transcriptService: TranscriptService,
    private dialogRef: MatDialogRef<ViewRespondentComponent>,
    @Inject(MAT_DIALOG_DATA) private data: IRespodentDialogData
  ) {}

  ngOnInit() {
    this.loadResources(this.data.respondentId);
    this.groupedThemes = this.data.groupedThemes;
  }

  ngOnDestroy() {
    this.revokeAudioData();
  }

  navigateBack() {
    const currentIndex = this.data.respondents.findIndex(
      (r) => r.respondentId === this.selectedRespondent.respondentId
    );

    const previousIndex =
      currentIndex === 0 ? this.data.respondents.length - 1 : currentIndex - 1;

    this.loadResources(this.data.respondents[previousIndex].respondentId);
  }

  navigateForward() {
    const currentIndex = this.data.respondents.findIndex(
      (r) => r.respondentId === this.selectedRespondent.respondentId
    );

    const nextIndex =
      currentIndex === this.data.respondents.length - 1 ? 0 : currentIndex + 1;

    this.loadResources(this.data.respondents[nextIndex].respondentId);
  }

  navigateCitation(citation: IRespondentAnswerReference) {
    this.audioStartTime = citation.timestampStart
      ? citation.timestampStart / 1000
      : 0;
  }

  confirm() {
    const changedAnswers = Object.entries(this.answerUpdates).map(
      ([key, answer]) => {
        const [respondentId, questionId] = key.split('_');
        return {
          respondent_id: respondentId,
          question_id: questionId,
          answer,
        };
      }
    );

    this.dialogRef.close(changedAnswers);
  }

  answerModification(respondentAnswer: IRespondentAnswer, event: Event) {
    const target = event.target as HTMLTextAreaElement;

    this.answerUpdates[
      `${respondentAnswer.respondent_id}_${respondentAnswer.question_id}`
    ] = target.value;
  }

  private loadResources(respondentId: string) {
    this.loading = true;

    const respondent = this.data.respondents.find(
      (r) => r.respondentId === respondentId
    );
    this.selectedRespondent = { ...respondent };
    this.transcriptFile = undefined;
    this.revokeAudioData();
    this.transcriptChunks = [];

    if (!this.selectedRespondent.processedTranscript) {
      this.loading = false;
      return;
    }

    const audioRequest$ = this.selectedRespondent.audio
      ? this.fileStoreService.getExpertAudioFileContents(
          this.selectedRespondent.audio.fileKey
        )
      : of(null);

    combineLatest([
      this.transcriptService.getTranscriptChunks(respondentId),
      audioRequest$,
    ]).subscribe(([content, audio]) => {
      this.transcriptChunks = content;
      this.transcriptFile = this.selectedRespondent.processedTranscript;

      if (audio) {
        this.audioData = URL.createObjectURL(audio);
      }

      this.audioStartTime = 0;

      this.loading = false;
    });
  }

  private revokeAudioData() {
    if (this.audioData) {
      URL.revokeObjectURL(this.audioData);
      this.audioData = undefined;
    }
  }
}
